import { ILORPContactDetails } from '@src/components/lorp/lorp-contact-details/LORPContactDetailsStep';
import {
  RETURN_METHOD,
  RETURN_METHOD_TO_SHIPMENT_MAP,
} from '@src/constants/general';
import { FULFILLMENT_TYPE } from '../constants/status';
import { IReturnShipments } from '../stores/returnStore';
import {
  ItemToReturn,
  OrderDetailsLineItemSessionObj,
  OrderDetailsSessionDataV2,
  PolicyValidatorShipping,
  PolicyValidatorShippingObject,
  ReturnsFraudPrompts,
  ShippingInfoDict,
} from '../models/returns';
import {
  IBuildSsReturnCreate,
  ShipFromAddress,
} from '../models/finalize-return';
import {
  ICreateReturnRequest,
  IReturnEligibilityRequest,
  IReturnEligibilityResponse,
  HrtvResponseLineItem,
  IRefundRequest,
  ISorisFinalizeRequest,
  IAprissVerifyObject,
  EligibilityItemRequest,
  CreateReturnShipment,
  HrtvRequestLineItem,
  AprissFinalizeRequest,
  IShipmentType,
  NewVerifyDecision,
  PolicyValidatorShipment,
} from '../models/api';
import {
  IDeliveryAddress,
  IFulfillmentGroup,
  IShipment,
} from '../models/common';
import { ReturnFraudType } from '../constants/analytics';
import { getFinalizeReturnShipments } from './finalizeReturnUtil';
import { getFeatureSwitchStatic } from './general';
import { getItemToReturnItemsFromShipment } from './itemsToReturnUtils';
import { fetchRefundEstimationAPI } from './api-helpers';
import { TagPolicyValidatorAllItemsIneligible } from './analytics-tagging';

/**
 * NETWORK: apply messaging for to network related API issues such as 5xx responses
 * GENERIC: generic error for non-network related issues
 * NONE: disable alert component
 */
export enum PolicyValidatorErrors {
  NETWORK = 'NETWORK',
  GENERIC = 'GENERIC',
  MULTI_SHIPMENTS = 'MULTI_SHIPMENTS',
  HAS_INELIGIBLE_ITEMS = 'HAS_INELIGIBLE_ITEMS',
  SSR_UPS_ERR_1002 = 'SSR_UPS_ERR_1002',
  NONE = '',
}

/**
 * Helper function to build request payload for policy validator API
 * @param returnItemsList List of items used to parse into request
 * @param deliveryDictionary used to read the item delivery state
 * @param fulfillmentGroups used as a fallback source for masked guest addresses
 * @returns
 */
export const generatePolicyValidatorRequest = (
  returnItemsList: ItemToReturn[] = [],
  deliveryDictionary: ShippingInfoDict = {},
  fulfillmentGroups: IFulfillmentGroup[] = [],
): HrtvRequestLineItem[] => {
  // OMS masks guest delivery addresses, but preserves the original state on the fulfillment group.
  const fallbackStateCd = fulfillmentGroups?.[0]?.postalDetails?.state || '';
  return returnItemsList.map((item: ItemToReturn) => ({
    lineItemId: item.lineItemId || '',
    mVendorNumber: item.mVendorNumber || '',
    depNbr: item.depNbr || '',
    classNumber: item.classNumber || '',
    subClass: item.itemSubClass || '',
    stateCd:
      deliveryDictionary?.[item.lineItemId || '']?.shippingAddress
        ?.postalDetails?.state || fallbackStateCd,
    itemDescription: item.itemDescription || '',
    itemCost: item.itemCost || 0,
    unitCost: item.unitCost || '',
    sku: item.sku || '',
    upcCode: item.upcCode || '',
    rtvReasonCd: item.returnReasonCode || '',
    isDFCSourced: item.isDFCSourced || false,
    vendorPartNumber: item.vendorPartNumber || '',
    vendorName: item.vendorName || '',
    fulfillmentVendorNumber: item.fulfillmentVendorNumber || '',
    hazmatItem: item.hazmatItem,
    shipmentType:
      RETURN_METHOD_TO_SHIPMENT_MAP[
        item.returnMethod || RETURN_METHOD.UPS_DROP_OFF
      ],
    returnQuantity: item.returnQuantity || 0,
  }));
};

/**
 * Helper function to build verify object for request payload for policy validator API
 * @param orderDetails Order Details v2 from OMS to parse into request
 */
export const generatePolicyValidatorVerifyObject = (
  orderDetails: OrderDetailsSessionDataV2,
  returnOrderDetails: IReturnEligibilityResponse,
  returnItemsList: ItemToReturn[] = [],
  shipping: PolicyValidatorShipping,
): IAprissVerifyObject => {
  return {
    locationId: orderDetails.storeNumber.toString(),
    order: {
      orderId: orderDetails.orderNumber,
      emailId: orderDetails.emailId,
      totalAmount: orderDetails.grandTotalAmount,
      currency: 'USD',
      lineItems: returnItemsList.map((item) => ({
        lineNumber: item.lineItemId,
        chargeIdentifier: item.chargeIdentifier,
        purchaseDate: orderDetails.createDateTime,
        sku: item.sku,
        quantity: item.returnQuantity,
        lineAmount: item.itemCost,
        lineTotalAmount: item.totalPrice,
        returnReason: returnOrderDetails.reasonCodes.find(
          (el) => el.code === item.returnReasonCode,
        )?.description,
        shipmentType:
          RETURN_METHOD_TO_SHIPMENT_MAP[
            item.returnMethod || RETURN_METHOD.UPS_DROP_OFF
          ],
      })),
      orderDate: orderDetails.createDateTime,
      chargeTransactions: orderDetails.chargeTransactions,
      shipping,
    },
  };
};

export const checkForIneligibleItems = (
  shipments: PolicyValidatorShipment[],
) => {
  const hasIneligibleItem = shipments.some((shipment) =>
    shipment.lineItems.some((item) => item.itemEligibility === false),
  );
  if (hasIneligibleItem) {
    TagPolicyValidatorAllItemsIneligible();
  }
  return hasIneligibleItem;
};

const mapDeliveryAddressToShipToAddress = (
  deliveryAddress: IDeliveryAddress | undefined = {},
  emailId = '',
) => {
  return {
    lastName: deliveryAddress.name?.lastName,
    firstName: deliveryAddress.name?.firstName,
    addressLine1: deliveryAddress.postalDetails?.addressLine1,
    addressLine2: deliveryAddress.postalDetails?.addressLine2,
    city: deliveryAddress.postalDetails?.city,
    state: deliveryAddress.postalDetails?.state,
    country: deliveryAddress.postalDetails?.country,
    zipCode: deliveryAddress.postalDetails?.zipCode,
    dayPhone: deliveryAddress.phoneNumbers?.dayPhone,
    mobilePhone: deliveryAddress.phoneNumbers?.mobilePhone,
    emailId,
  };
};

export const buildReturnEligibilityRequest = (
  orderDetailsData: OrderDetailsSessionDataV2,
  isGuest: boolean,
  items: OrderDetailsLineItemSessionObj[],
  enableSCAC?: boolean,
): IReturnEligibilityRequest => ({
  orderId: orderDetailsData.orderNumber,
  emailId: orderDetailsData.emailId,
  net: orderDetailsData.net || '',
  isGuest,
  lineItems: items.map<EligibilityItemRequest>((item) => ({
    lineId: item.lineId,
    classId: item.classId,
    subClassId: item.subClassId,
    itemWeight: item.itemWeight,
    unitPrice: item.unitPrice,
    departmentNumber: item.departmentNumber,
    customBlindProductType: item.customBlindProductType || '',
    scac: enableSCAC ? item.scac || '' : undefined,
    skuNumber: enableSCAC ? item.skuNumber || '' : undefined,
    upcCode: item.upcCode || '',
    mVendorNumber: item.vendorNumber || '',
  })),
});

export const buildRefundEstimationRequest = (
  orderDetailsV2: OrderDetailsSessionDataV2 | undefined,
  selectedReturnItems: ItemToReturn[],
): IRefundRequest => {
  return {
    orderId: orderDetailsV2?.orderNumber || '',
    shippingCharge: orderDetailsV2?.shippingCharge || 0,
    lineItems: selectedReturnItems.map((item) => ({
      itemId: item.lineItemId || '',
      shippingCharge: item.shippingCharge || 0,
      quantity: item.returnQuantity,
      fulfillmentType:
        orderDetailsV2?.deliveryDictionary?.[item.lineItemId]?.fulfillmentType,
    })),
  };
};

const FRAUD_KEY_MAPPER: { [key: string]: ReturnFraudType } = {
  Warned: 'WARNED',
  Denied: 'DENIED',
};

/**
 * Checks if Policy Validator response returned a fraud or warning message
 * @param response Policy Validator API response
 * @returns WARNING, DENIED, or undefined
 */
export const checkFraudType = (
  multiVerifyDecision: NewVerifyDecision | undefined,
): ReturnFraudType | undefined => {
  // fraud decision values can be 'Approved', 'Warned', or 'Denied'
  const decision = multiVerifyDecision?.finalDecision || '';
  return FRAUD_KEY_MAPPER[decision];
};

/**
 * Helper function to extract denial or warning message from TRE
 * @param response Policy Validator response payload
 * @returns message returned by TRE
 */
export const getFraudMessage = (
  multiVerifyDecision: NewVerifyDecision | undefined,
) => multiVerifyDecision?.message?.consumer || '';

/**
 * Maps orderDetails delivery addresses to the request needed for policy validator
 * @param deliveryAddresses list of delivery addresses from order details from all fulfillment groups
 * @returns mapped list with sequence number and variable name changes
 */
const buildShippingsList = (
  deliveryAddresses: IDeliveryAddress[],
): PolicyValidatorShippingObject[] =>
  deliveryAddresses.map((deliveryAddress, index) => ({
    name: {
      firstName: deliveryAddress.name?.firstName || '',
      lastName: deliveryAddress.name?.lastName || '',
    },
    postalDetails: {
      addressLine1: deliveryAddress.postalDetails?.addressLine1 || '',
      addressLine2: deliveryAddress.postalDetails?.addressLine2,
      addressLine3: deliveryAddress.postalDetails?.addressLine3,
      addressLine4: deliveryAddress.postalDetails?.addressLine4,
      addressLine5: deliveryAddress.postalDetails?.addressLine5,
      city: deliveryAddress.postalDetails?.city || '',
      postalCode: deliveryAddress.postalDetails?.zipCode || '',
      stateCode: deliveryAddress.postalDetails?.state || '',
    },
    sequenceNumber: index.toString(),
  }));

/**
 *
 * @param returnItems list of items selected for return
 * @param deliveryDictionary dictionary used to look up shipping address via lineId
 * @returns a list of unique shipping addresses from the list of items being returned
 */
const getDeliveryAddressesFromSelectedItems = (
  returnItems: ItemToReturn[],
  deliveryDictionary: ShippingInfoDict = {},
) => {
  const uniqueAddresses = new Set();
  return returnItems.flatMap((item) => {
    const address =
      deliveryDictionary?.[item.lineItemId || '']?.shippingAddress || {};

    // Convert the address object to a string for easy comparison
    const addressString = JSON.stringify(address);

    if (!uniqueAddresses.has(addressString)) {
      // If the address is not in the Set, add it to the Set and return it
      uniqueAddresses.add(addressString);
      return [address];
    }

    return []; // Return an empty array if the address is a duplicate
  });
};

export const generateShippingRequestObject = (
  orderDetails: OrderDetailsSessionDataV2 = {} as OrderDetailsSessionDataV2,
  returnItems: ItemToReturn[] = [],
): PolicyValidatorShipping => {
  const shippingCost =
    orderDetails.shippingCharge + orderDetails.deliveryCharge;
  const deliveryAddresses = getDeliveryAddressesFromSelectedItems(
    returnItems,
    orderDetails.deliveryDictionary,
  );
  const shippings = buildShippingsList(deliveryAddresses);
  return {
    shippingCost,
    shippings,
  };
};

/**
 * Maps to only the necessary lineItem fields required for FinalizeReturn
 * @param lineItems LineItems from OrderDataV2
 * @returns LineItem list with required fields
 */
const mapToFinalizeReturnLineItems = (
  orderDetailsV2: OrderDetailsSessionDataV2 = {} as OrderDetailsSessionDataV2,
) => {
  const lineItems = orderDetailsV2.lineItems.filter(
    (item) =>
      orderDetailsV2.deliveryDictionary?.[item.lineId || '']?.shippingAddress,
  );
  return lineItems.map((lineItem) => {
    const deliveryAddress =
      orderDetailsV2.deliveryDictionary?.[lineItem.lineId || '']
        .shippingAddress;
    return {
      itemWeight: lineItem.itemWeight,
      mVendorNumber: lineItem.vendorNumber,
      shipToAddress: mapDeliveryAddressToShipToAddress(
        deliveryAddress,
        orderDetailsV2.emailId,
      ),
      lineItemId: lineItem.lineId,
    };
  });
};

const mapToSSOrderViewBillingAddress = (
  billingAddress: OrderDetailsSessionDataV2['billingAddress'] = {} as OrderDetailsSessionDataV2['billingAddress'],
) => ({
  lastName: billingAddress.name?.lastName,
  firstName: billingAddress.name?.firstName,
  addressLine1: billingAddress.postalDetails?.addressLine1,
  addressLine2: billingAddress.postalDetails?.addressLine2,
  city: billingAddress.postalDetails?.city,
  state: billingAddress.postalDetails?.state,
  zipCode: billingAddress.postalDetails?.zipCode,
  dayPhone: billingAddress.primaryPhone?.number,
  emailId: billingAddress.emailId,
});

/**
 * Mapper function used to easily pass new fields to <FinalizeReturn>
 * @param orderDetailsV2 New OMS response
 */
export const getOrderDetailsReturnCreateFields = (
  orderDetailsV2: OrderDetailsSessionDataV2,
) => ({
  orderId: orderDetailsV2.orderNumber,
  emailId: orderDetailsV2.emailId,
  locationId: orderDetailsV2.storeNumber,
  orderDate: orderDetailsV2.createDateTime,
  orderVersion: orderDetailsV2.versionId,
  billingAddress: mapToSSOrderViewBillingAddress(orderDetailsV2.billingAddress),
  lineItemsInfo: mapToFinalizeReturnLineItems(orderDetailsV2),
  net: orderDetailsV2.net,
});

export const getFieldByLineItemId = (
  arrayLineItem: HrtvResponseLineItem[],
  lineItemId: string,
  fieldName: string,
) => {
  const lineItem = arrayLineItem.find((item) => item.lineItemId === lineItemId);
  if (lineItem && fieldName in lineItem) {
    return lineItem[fieldName as keyof HrtvResponseLineItem];
  }
  return null;
};

const mapDeliveryDictShippingAddressToFinalizeShipToAddress = (
  shippingAddress: IDeliveryAddress = {} as IDeliveryAddress,
  email: string,
) => {
  return {
    lastName: shippingAddress.name?.lastName,
    firstName: shippingAddress.name?.firstName,
    addressLine1: shippingAddress.postalDetails?.addressLine1,
    addressLine2: shippingAddress.postalDetails?.addressLine2,
    city: shippingAddress.postalDetails?.city,
    state: shippingAddress.postalDetails?.state,
    country: shippingAddress.postalDetails?.state,
    zipCode: shippingAddress.postalDetails?.zipCode,
    dayPhone: shippingAddress.phoneNumbers?.dayPhone,
    mobilePhone: shippingAddress.phoneNumbers?.mobilePhone,
    emailId: email,
  };
};

export const getLORPShipFromAddress = (
  lorpContactDetails: ILORPContactDetails,
  fulfillmentGroups: IFulfillmentGroup[],
  selectedReturnItems: ItemToReturn[],
): ShipFromAddress => {
  // Will need to be updated if we begin allowing multiple LORP item returns
  const selectedLORPItemId = selectedReturnItems?.[0]?.lineItemId;
  const lorpItemShippingDetails = fulfillmentGroups.find((fulfillmentGroup) =>
    fulfillmentGroup.lineItems?.find(
      (lineItem) => lineItem.lineId === selectedLORPItemId,
    ),
  )?.shippingAndHandling?.deliveryAddress;
  const { mobilePhone, dayPhone } = lorpItemShippingDetails?.phoneNumbers || {};

  return {
    firstName: lorpContactDetails.firstName,
    lastName: lorpContactDetails.lastName,
    addressLine1: lorpContactDetails.address?.addressLine1,
    addressLine2: lorpContactDetails.address?.addressLine2,
    city: lorpContactDetails.address?.city,
    state: lorpContactDetails.address?.state,
    zipCode: lorpContactDetails.address?.zipCode,
    country: lorpContactDetails.address?.country,
    dayPhone,
    mobilePhone,
    pickupPhone: lorpContactDetails.phoneNumber,
    emailId: lorpContactDetails.email,
  };
};

/**
 * Given a list of lineItems, return the first STH address found,
 *  if no STH lineItem is found return first item address from the list
 * @param orderLineList lineItems
 * @returns appropriate address given the requirements
 */
export const getSsReturnCreateShipFromAddress = (
  emailId: string,
  selectedReturnItems: ItemToReturn[],
  deliveryDictionary?: ShippingInfoDict,
): ShipFromAddress => {
  // shipToAddress originaly came from deliveryDictionary[lineId].shippingAddress(mapToFinalizeReturnLineItems line 337) and emailId comes from orderDetails
  const sthLineItem = selectedReturnItems?.find(
    (orderLine) =>
      deliveryDictionary?.[orderLine?.lineItemId || '']?.fulfillmentType ===
      FULFILLMENT_TYPE.STH,
  );
  const targetLineItemId = sthLineItem
    ? sthLineItem?.lineItemId
    : selectedReturnItems?.[0]?.lineItemId;
  const shipToAddress =
    deliveryDictionary?.[targetLineItemId || '']?.shippingAddress;
  return mapDeliveryDictShippingAddressToFinalizeShipToAddress(
    shipToAddress,
    emailId,
  );
};

/**
 * Calculate the total refund amount for SORIS (in-store) items only
 * This function calls the RefundEstimationAPI to get the accurate refund (including tax, shipping adjustments, etc.)
 * @param orderDetailsV2 Order details containing shipping and order information
 * @param sorisItems SORIS items for return (already filtered)
 * @returns Total refund amount as a string from the API, or throws an error if the API call fails
 */
export const calculateSorisRefundAmount = async (
  orderDetailsV2: OrderDetailsSessionDataV2,
  sorisItems: ItemToReturn[],
): Promise<string | undefined> => {
  // Build refund request for SORIS items only
  const refundRequest = buildRefundEstimationRequest(
    orderDetailsV2,
    sorisItems,
  );

  try {
    const refundValues = await fetchRefundEstimationAPI(refundRequest);
    if (!refundValues) {
      throw new Error('RefundEstimationAPI: Empty 2xx Response');
    }
    return refundValues.total;
  } catch (error) {
    // Throw error to be handled by caller due to being a hard dependency
    throw new Error('Failed to fetch SORIS refund amount');
  }
};

export const buildFinalizeSORISRequest = async (
  orderDetailsV2: OrderDetailsSessionDataV2,
  selectedReturnItems: ItemToReturn[],
  isGuest: boolean,
  existingRefundTotal: string,
  orderVersion: string,
): Promise<ISorisFinalizeRequest> => {
  const {
    orderNumber: orderId,
    emailId,
    net = '',
    storeNumber: locationId,
    createDateTime: orderDate,
    billingAddress,
  } = orderDetailsV2;

  // Filter to only SORIS items for the request body
  const sorisItems = selectedReturnItems.filter(
    (item) => item.returnMethod === RETURN_METHOD.SORIS,
  );

  const isAllSorisItems = selectedReturnItems.every(
    (item) => item.returnMethod === RETURN_METHOD.SORIS,
  );

  // If all items are SORIS, use existing refund total. Otherwise, recalculate for SORIS items only
  // TODO wrap this function in a try/catch when calling to handle API errors due to being a hard dependency
  const totalRefundAmount = isAllSorisItems
    ? existingRefundTotal
    : (await calculateSorisRefundAmount(orderDetailsV2, sorisItems)) || '';

  return {
    orderId,
    emailId,
    net,
    locationId: locationId.toString(),
    orderVersion,
    isGuest,
    orderDate,
    totalRefundAmount,
    customer: {
      email: emailId,
      firstName: billingAddress.name?.firstName || '',
    },
    orderLines: sorisItems.map((item: ItemToReturn) => ({
      lineItemId: item.lineItemId,
      returnQuantity: item.returnQuantity,
      returnReasonCode: item.returnReasonCode,
      returnWindowEndDate: item.returnWindowEndDate,
      manufacturerName: item.brandName || '',
      shortItemDescription: item.itemDescription || '',
      internetSku: item.omsId?.toString() || '',
      storeSku: item.sku?.toString() || '',
      itemTotal: (item.returnQuantity * item.itemCost).toFixed(2),
    })),
  };
};

// Builds request used to call ss return create endpoint
export const buildSsReturnCreateRequest = ({
  orderDetailsV2,
  isGuest,
  policyValidatorET,
  deliveryDictionary,
  returnShipments,
  selectedItems,
  shipmentType,
  orderVersion,
  isKeepIt,
}: IBuildSsReturnCreate): ICreateReturnRequest => {
  const {
    orderId,
    emailId,
    billingAddress,
    locationId,
    orderDate,
    net = '',
  } = getOrderDetailsReturnCreateFields(orderDetailsV2);

  const modifiedReturnShipments = returnShipments?.filter(
    (shipment) =>
      shipment.shipmentType !== 'PHASE_0_SORIS' &&
      shipment.shipmentType !== 'SORIS',
  );
  const shipmentsRequest = getFinalizeReturnShipments(
    emailId,
    deliveryDictionary,
    modifiedReturnShipments || [],
    selectedItems,
    orderDetailsV2,
  );

  return {
    requestedShipmentType: shipmentType,
    orderId,
    et: policyValidatorET,
    billingAddress,
    emailId,
    isGuest,
    net,
    shipments: shipmentsRequest,
    locationId: locationId.toString(),
    orderDate,
    orderVersion: orderVersion || '',
    isKeepIt: isKeepIt ?? false,
  };
};

export const buildAprissFinalizeRequest = ({
  orderDetailsV2,
  policyValidatorET,
  returnOrderId,
  orderDateForReturn,
  shipmentType,
  identifier,
  isGuest,
}: {
  orderDetailsV2: OrderDetailsSessionDataV2;
  policyValidatorET: string;
  returnOrderId: string;
  orderDateForReturn: string;
  shipmentType: IShipmentType;
  identifier: '0' | '1' | '2' | '3';
  isGuest: boolean;
}): AprissFinalizeRequest => {
  return {
    shipmentType,
    identifier,
    originalOrder: {
      orderId: orderDetailsV2.orderNumber,
      locationId: orderDetailsV2.storeNumber.toString(),
      orderDate: orderDetailsV2.createDateTime,
      et: policyValidatorET,
      emailId: orderDetailsV2.emailId,
      isGuest,
      net: orderDetailsV2.net || '',
    },
    returnOrder: {
      orderId: returnOrderId,
      returnDate: orderDateForReturn,
    },
  };
};

export const getReturnsFraudPrompts = (
  bodyMessage: string,
  closeModal: () => void,
  proceedToNextStep: () => void,
): ReturnsFraudPrompts => ({
  WARNED: {
    title: 'We May Decline Future Returns',
    buttonMessage: 'Continue',
    bodyMessage,
    onClick: () => {
      closeModal();
      proceedToNextStep();
    },
  },
  DENIED: {
    title: "We're Unable to Accept This Return",
    buttonMessage: 'Close',
    bodyMessage,
    onClick: closeModal,
  },
  WARNEDINVALID: {
    title: 'We May Decline Future Returns',
    buttonMessage: 'Close',
    bodyMessage,
    onClick: closeModal,
  },
});

interface ShipmentsDictionary {
  [key: string]: {
    shipment: IShipment[] | undefined;
    isShippedBoxesKnown?: boolean;
  };
}

/**
 * will generate a mapping that will be used to determine if # of boxes shipped are known
 * currently only supported by DFC orders
 * @param orderDetailsV2 orders object
 * @returns lineId key -> FG.shippingAndHandling.shipments.shipment mapping
 */
const createShippingsMap = (orderDetailsV2?: OrderDetailsSessionDataV2) => {
  const shippingAndHandlingMap: ShipmentsDictionary = {};
  orderDetailsV2?.fulfillmentGroups?.forEach((fg) => {
    fg.lineItems?.forEach((item) => {
      shippingAndHandlingMap[item.lineId] = {
        shipment: fg.shippingAndHandling?.shipments?.shipment,
        isShippedBoxesKnown: fg.isShippedBoxesKnown,
      };
    });
  });
  return shippingAndHandlingMap;
};

export const getMaxBoxesForShipment = (
  shipment: IReturnShipments,
  selectedItems: ItemToReturn[],
  orderDetailsV2?: OrderDetailsSessionDataV2,
) => {
  const fsBoxesValue = Number(getFeatureSwitchStatic('boxesPerItem', false));
  const boxesPerItem = fsBoxesValue && fsBoxesValue > 0 ? fsBoxesValue : 1;
  const shipmentLineItems = getItemToReturnItemsFromShipment(
    shipment,
    selectedItems,
  );
  const maxBoxes = getNumberOfReturnableBoxes(
    shipmentLineItems,
    boxesPerItem,
    orderDetailsV2,
  );
  // Capping the maximum number of boxes to 10 per shipment
  return maxBoxes > 10 ? 10 : maxBoxes;
};

export const getNumberOfReturnableBoxes = (
  selectedReturnItems: ItemToReturn[],
  boxesPerItem: number,
  orderDetailsV2?: OrderDetailsSessionDataV2,
) => {
  // if we have the new dictionary object
  if (orderDetailsV2?.orderShipmentsInfo) {
    const usedIndexes = new Set<string>();
    let nonDFCItemCount = 0;
    let shipmentDFCCount = 0;
    const { itemShipmentsDictionary, shipmentsArray } =
      orderDetailsV2.orderShipmentsInfo;
    // Iterate through each selected item and add the shipment to the usedShipments set
    selectedReturnItems.forEach((item) => {
      const index = Number(itemShipmentsDictionary[item.lineItemId]);
      const isShippedBoxesKnown = shipmentsArray[index].isShippedBoxesKnown;
      if (!isShippedBoxesKnown) {
        nonDFCItemCount += item.returnQuantity;
        return;
      }
      if (!usedIndexes.has(index.toString())) {
        usedIndexes.add(index.toString());
        shipmentDFCCount += shipmentsArray[index].shipment?.length || 0;
      }
    });

    // And take the quantity of the items skipped(non-dfc) and multiply by the boxes per item
    const totalNonDFCShipments = nonDFCItemCount * boxesPerItem;
    return shipmentDFCCount + totalNonDFCShipments;
  }
  const shippingAndHandlingMap = createShippingsMap(orderDetailsV2);
  // Keep a set of all IShipment objects that have been used for DFC, this will prevent duplicate shipments from being counted
  const usedDFCShipments = new Set<IShipment[]>();
  let nonDFCItemCount = 0;
  // Iterate through each selected item and add the shipment to the usedShipments set
  selectedReturnItems.forEach((item) => {
    if (!shippingAndHandlingMap[item.lineItemId].isShippedBoxesKnown) {
      nonDFCItemCount += item.returnQuantity;
      return;
    }

    const shipmentsObj = shippingAndHandlingMap[item.lineItemId];
    usedDFCShipments.add(shipmentsObj.shipment || []);
  });
  // Now take the sum of the length of all the DFC shipments that were used
  const totalDFCShipments = Array.from(usedDFCShipments).reduce(
    (acc, next) => acc + next.length,
    0,
  );
  // And take the quantity of the items skipped(non-dfc) and multiply by the boxes per item
  const totalNonDFCShipments = nonDFCItemCount * boxesPerItem;

  const maxBoxes = totalDFCShipments + totalNonDFCShipments;
  // Capping the maximum number of boxes to 10 per shipment
  return maxBoxes > 10 ? 10 : maxBoxes;
};

export const getItemsByShipment = (
  shipment: CreateReturnShipment | undefined,
  selectedReturnItems: ItemToReturn[],
) => {
  if (!shipment?.itemIds) {
    return [];
  }
  return selectedReturnItems.filter((item) =>
    shipment.itemIds.includes(item.lineItemId),
  );
};

export const getItemsByHrtvShipment = (
  shipment?: IReturnShipments,
  selectedReturnItems?: ItemToReturn[],
) => {
  if (!shipment?.lineItems) {
    return [];
  }
  return selectedReturnItems?.filter((item) =>
    shipment.lineItems.some(
      (lineItem) => item.lineItemId === lineItem.lineItemId,
    ),
  );
};

export const generateInStoreShipment = (
  selectedItems: ItemToReturn[],
  existingShipments: IReturnShipments[],
): IReturnShipments => {
  return {
    shipmentId:
      (existingShipments[existingShipments.length - 1]?.shipmentId || 0) + 1,
    shipmentType: 'PHASE_0_SORIS',
    shipToAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      emailId: '',
      state: '',
      zipCode: '',
      country: '',
      dayPhone: '',
      contactName: '',
      shipToName: '',
    },
    lineItems: selectedItems
      .filter((item) => item.returnMethod === RETURN_METHOD.IN_STORE)
      .map((item) => ({
        lineItemId: item.lineItemId,
        returnDispCode: '',
        rtvTokenId: '',
        returnPolicyId: 0,
        itemEligibility: true,
        vendorReturnAddress: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
          dayPhone: '',
          contactName: '',
          shipToName: '',
        },
        statusCodes: '',
      })),
  };
};

/**
 * Finds a CreateReturnShipment that matches an IReturnShipments by comparing lineItemIds
 * @param returnShipment The IReturnShipments to find a match for
 * @param shipments Array of CreateReturnShipment to search through
 * @returns The matching CreateReturnShipment or undefined
 */
export const findMatchingCreateReturnShipment = (
  returnShipment?: IReturnShipments,
  shipments?: CreateReturnShipment[],
) => {
  return shipments?.find((shipment) =>
    returnShipment?.lineItems.some((lineItem) =>
      shipment.itemIds.includes(lineItem.lineItemId),
    ),
  );
};

/**
 * Finds an IReturnShipments that matches a CreateReturnShipment by comparing lineItemIds
 * @param createReturnShipment The CreateReturnShipment to find a match for
 * @param returnShipments Array of IReturnShipments to search through
 * @returns The matching IReturnShipments or undefined
 */
export const findMatchingReturnShipment = (
  createReturnShipment?: CreateReturnShipment,
  returnShipments?: IReturnShipments[],
) => {
  return returnShipments?.find((returnShipment) =>
    returnShipment.lineItems.some((lineItem) =>
      createReturnShipment?.itemIds.includes(lineItem.lineItemId),
    ),
  );
};
